import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { medicalApi } from "../../services/medicalApi";
import { usePermissions } from "../../hooks/usePermissions";
import { StatusBadge } from "../../components/StatusBadge";
import { PageHelpBanner } from "../../components/PageHelpBanner";
import { formatApiErrorDetail } from "../../lib/api";
import { useLocalization } from "../../hooks/useLocalization";

export default function MedicalChecksPage() {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { canManageMedicalChecks } = usePermissions();
  const [checks, setChecks] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [editingFrequency, setEditingFrequency] = useState(null);
  const [frequencyValue, setFrequencyValue] = useState("12");
  const [frequencyReason, setFrequencyReason] = useState("");
  const [acknowledging, setAcknowledging] = useState(null);
  const [completedDate, setCompletedDate] = useState("");

  const load = () => {
    medicalApi.listChecks(statusFilter || undefined).then((r) => setChecks(r.data)).catch(() => setChecks([]));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (canManageMedicalChecks) load(); }, [canManageMedicalChecks, statusFilter]);

  if (!canManageMedicalChecks) return <p className="text-sm text-gray-500" data-testid="medical-checks-access-denied">{t("common.accessDenied")}</p>;

  const saveFrequency = async (employeeId) => {
    try {
      await medicalApi.setFrequency(employeeId, { frequency_months: Number(frequencyValue), reason: frequencyReason || t("mgmtMedical.managementOverride") });
      toast.success(t("mgmtMedical.frequencyUpdated"));
      setEditingFrequency(null); setFrequencyReason(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const saveAcknowledgement = async (employeeId) => {
    if (!completedDate) { toast.error(t("mgmtMedical.enterCompletionDate")); return; }
    try {
      await medicalApi.acknowledgeCompleted(employeeId, { completed_date: completedDate });
      toast.success(t("mgmtMedical.checkAcknowledged"));
      setAcknowledging(null); setCompletedDate(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-5" data-testid="medical-checks-page">
      <PageHelpBanner pageKey="medical-checks" title={t("pageHelp.defaultTitle")}>
        <p dangerouslySetInnerHTML={{ __html: t("mgmtMedical.checksHelp") }} />
      </PageHelpBanner>
      <div>
        <h2 className="text-2xl font-bold tracking-tight text-gray-900">{t("mgmtMedical.checksTitle")}</h2>
        <p className="text-sm text-gray-500">{t("mgmtMedical.checksSubtitle")}</p>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        {["", "OVERDUE", "DUE_SOON", "ON_TRACK", "APPOINTMENT_SCHEDULED", "NOT_YET_ASSIGNED"].map((s) => (
          <button key={s || "ALL"} data-testid={`medical-filter-${s || "ALL"}`} onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${statusFilter === s ? "bg-gray-900 text-white border-gray-900" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
            {s || t("mgmtMedical.allFilter")}
          </button>
        ))}
      </div>

      <div className="bg-white rounded-xl border border-gray-200 overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-gray-50 text-gray-500 text-left">
            <tr><th className="px-4 py-3 font-medium">{t("mgmtPayroll.colEmployee")}</th><th className="px-4 py-3 font-medium">{t("mgmtMedical.colFrequency")}</th><th className="px-4 py-3 font-medium">{t("mgmtMedical.colLastCompleted")}</th><th className="px-4 py-3 font-medium">{t("mgmtMedical.colNextDue")}</th><th className="px-4 py-3 font-medium">{t("mgmtMedical.colScheduledAppt")}</th><th className="px-4 py-3 font-medium">{t("common.status")}</th><th className="px-4 py-3 font-medium">{t("mgmtMedical.colActions")}</th></tr>
          </thead>
          <tbody>
            {checks.map((c) => (
              <tr key={c.employee_id} className="border-t border-gray-100" data-testid={`medical-check-row-${c.employee_id}`}>
                <td className="px-4 py-2 font-semibold text-gray-800">
                  <button data-testid={`medical-view-profile-btn-${c.employee_id}`} onClick={() => navigate(`/management/employees/${c.employee_id}`)} className="hover:underline hover:text-blue-600 text-left">
                    {c.employee_name}
                  </button>
                </td>
                <td className="px-4 py-2 text-gray-600">
                  {editingFrequency === c.employee_id ? (
                    <span className="inline-flex items-center gap-1">
                      <input data-testid={`frequency-months-input-${c.employee_id}`} value={frequencyValue} onChange={(e) => setFrequencyValue(e.target.value)} className="border border-gray-200 rounded px-2 py-1 text-xs w-14" />
                      <input data-testid={`frequency-reason-input-${c.employee_id}`} value={frequencyReason} onChange={(e) => setFrequencyReason(e.target.value)} placeholder={t("mgmtMedical.reasonPlaceholder")} className="border border-gray-200 rounded px-2 py-1 text-xs w-28" />
                      <button data-testid={`frequency-save-btn-${c.employee_id}`} onClick={() => saveFrequency(c.employee_id)} className="px-2 py-1 rounded text-xs font-semibold bg-blue-600 text-white">{t("common.save")}</button>
                    </span>
                  ) : (
                    <button data-testid={`frequency-edit-btn-${c.employee_id}`} onClick={() => { setEditingFrequency(c.employee_id); setFrequencyValue(String(c.frequency_months)); }} className="hover:underline">{c.frequency_months} {t("mgmtMedical.monthsAbbrev")}</button>
                  )}
                </td>
                <td className="px-4 py-2 text-gray-600">{c.last_completed_date || "-"}</td>
                <td className="px-4 py-2 text-gray-600">{c.next_due_date || "-"}</td>
                <td className="px-4 py-2 text-gray-600">{c.scheduled_appointment_date || "-"}</td>
                <td className="px-4 py-2"><StatusBadge status={c.status} /></td>
                <td className="px-4 py-2">
                  {acknowledging === c.employee_id ? (
                    <span className="inline-flex items-center gap-1">
                      <input data-testid={`completed-date-input-${c.employee_id}`} type="date" value={completedDate} onChange={(e) => setCompletedDate(e.target.value)} className="border border-gray-200 rounded px-2 py-1 text-xs" />
                      <button data-testid={`acknowledge-save-btn-${c.employee_id}`} onClick={() => saveAcknowledgement(c.employee_id)} className="px-2 py-1 rounded text-xs font-semibold bg-green-600 text-white">{t("common.save")}</button>
                    </span>
                  ) : (
                    <button data-testid={`acknowledge-completed-btn-${c.employee_id}`} onClick={() => setAcknowledging(c.employee_id)} className="px-2 py-1 rounded text-xs font-semibold border border-gray-200 hover:bg-gray-50">{t("mgmtMedical.acknowledgeCompleted")}</button>
                  )}
                </td>
              </tr>
            ))}
            {checks.length === 0 && <tr><td colSpan={7} className="px-4 py-6 text-center text-gray-500" data-testid="no-medical-checks-row">{t("mgmtMedical.noEmployeesFound")}</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
